import {
  OrderDetailsResponse,
  AuthConfigResponse,
  OrdersResponse,
  OrdersSummary,
  SessionResponse,
  TelegramAuthPayload,
  AdminStats,
  AdminOrderItem,
  AdminUserItem,
  AdminPayoutItem,
  AdminWithdrawalItem,
  AdminIntegration,
  AdminRaffleItem,
  AdminClientItem,
  RaffleConfig,
  RaffleStats,
  RaffleWithTransactions,
  PaginatedResponse,
  UserCardResponse,
} from './types';

const JSON_HEADERS = {
  'Content-Type': 'application/json',
};

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    const message = await safeParseError(response);
    throw new Error(message);
  }

  return response.json() as Promise<T>;
}

async function safeParseError(response: Response): Promise<string> {
  try {
    const payload = await response.json();
    if (payload && typeof payload.message === 'string') {
      return payload.message;
    }
  } catch {
    // Ignore parse errors
  }

  return `Request failed (${response.status})`;
}

export const api = {
  async getPublicConfig(): Promise<{ baseUrl: string; sdkUrl: string }> {
    const res = await fetch('/api/config');
    return handleResponse<{ baseUrl: string; sdkUrl: string }>(res);
  },

  async getSession(): Promise<SessionResponse> {
    const res = await fetch('/api/auth/session', { credentials: 'include' });
    if (!res.ok) {
      return { isAuthenticated: false };
    }

    return res.json();
  },

  async loginWithTelegram(payload: TelegramAuthPayload) {
    const res = await fetch('/api/auth/telegram', {
      method: 'POST',
      headers: JSON_HEADERS,
      credentials: 'include',
      body: JSON.stringify(payload),
    });

    return handleResponse<SessionResponse>(res);
  },

  async logout(): Promise<void> {
    const response: Response = await fetch('/api/auth/logout', {
      method: 'POST',
      headers: JSON_HEADERS,
      credentials: 'include',
    });
    if (!response.ok) {
      const message = await safeParseError(response);
      throw new Error(message);
    }
  },

  async getAuthConfig(): Promise<AuthConfigResponse> {
    const res = await fetch('/api/auth/config', { credentials: 'include' });
    return handleResponse<AuthConfigResponse>(res);
  },

  async getOrders(
    limit?: number,
    offset?: number,
    status?: string,
    currency?: string,
  ): Promise<OrdersResponse> {
    const params = new URLSearchParams();
    if (typeof limit === 'number') params.append('limit', String(limit));
    if (typeof offset === 'number') params.append('offset', String(offset));
    if (status) params.append('status', status);
    if (currency) params.append('currency', currency);
    const query = params.toString() ? `?${params.toString()}` : '';
    const res = await fetch(`/api/orders${query}`, { credentials: 'include' });
    return handleResponse<OrdersResponse>(res);
  },

  async getOrdersSummary(): Promise<OrdersSummary> {
    const res = await fetch('/api/orders/summary', { credentials: 'include' });
    return handleResponse<OrdersSummary>(res);
  },

  async getOrderDetails(orderId: string): Promise<OrderDetailsResponse> {
    const res = await fetch(`/api/orders/${orderId}/details`, { credentials: 'include' });
    return handleResponse<OrderDetailsResponse>(res);
  },

  async getBalance() {
    const res = await fetch('/api/balance', { credentials: 'include' });
    return handleResponse<{
      balanceUsdt: number;
      totalEarnedUsdt: number;
      totalWithdrawnUsdt: number;
    }>(res);
  },

  async getDepositAddress(): Promise<{ address: string }> {
    const res = await fetch('/api/balance/deposit-address', { credentials: 'include' });
    return handleResponse<{ address: string }>(res);
  },

  async getUserCard(): Promise<UserCardResponse> {
    const res = await fetch('/api/user/card', { credentials: 'include' });
    return handleResponse<UserCardResponse>(res);
  },

  async updateUserCard(payload: {
    displayName?: string | null;
    description?: string | null;
    bankCard?: string | null;
  }): Promise<UserCardResponse> {
    const res = await fetch('/api/user/card', {
      method: 'PUT',
      headers: JSON_HEADERS,
      credentials: 'include',
      body: JSON.stringify(payload),
    });
    return handleResponse<UserCardResponse>(res);
  },

  async uploadCardPhoto(file: File): Promise<{ photoUrl: string; key: string }> {
    const formData = new FormData();
    formData.append('photo', file);

    const res = await fetch('/api/user/card/photo', {
      method: 'POST',
      credentials: 'include',
      body: formData,
    });
    return handleResponse<{ photoUrl: string; key: string }>(res);
  },

  async getPublicCard(userId: string): Promise<UserCardResponse> {
    const res = await fetch(`/api/public/card/${userId}`);
    return handleResponse<UserCardResponse>(res);
  },

  async getBalanceHistory(limit?: number, offset?: number) {
    const params = new URLSearchParams();
    if (typeof limit === 'number') params.append('limit', limit.toString());
    if (typeof offset === 'number') params.append('offset', offset.toString());
    const query = params.toString() ? `?${params.toString()}` : '';
    const res = await fetch(`/api/balance/history${query}`, { credentials: 'include' });
    return handleResponse<{
      transactions: Array<{
        id: string;
        type: 'deposit' | 'withdrawal' | 'refund' | 'correction';
        amount: number;
        description: string | null;
        createdAt: string;
        orderId?: string | null;
      }>;
      total: number;
    }>(res);
  },

  async getWithdrawals(limit?: number, offset?: number) {
    const params = new URLSearchParams();
    if (typeof limit === 'number') params.append('limit', String(limit));
    if (typeof offset === 'number') params.append('offset', String(offset));
    const query = params.toString() ? `?${params.toString()}` : '';
    const res = await fetch(`/api/balance/withdrawals${query}`, { credentials: 'include' });
    return handleResponse<{
      withdrawals: Array<{
        id: string;
        amountUsdt: number;
        withdrawalAddress: string;
        status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
        txId: string | null;
        errorMessage: string | null;
        createdAt: string;
        completedAt: string | null;
      }>;
    }>(res);
  },

  async getMerchantPayouts(limit?: number) {
    const params = new URLSearchParams();
    if (typeof limit === 'number') params.append('limit', String(limit));
    const query = params.toString() ? `?${params.toString()}` : '';
    const res = await fetch(`/api/balance/merchant-payouts${query}`, { credentials: 'include' });
    return handleResponse<{
      payouts: Array<{
        id: string;
        orderId: string;
        merchantAddress: string;
        amountUsdt: number;
        status: 'pending' | 'processing' | 'queued' | 'sent' | 'completed' | 'failed';
        txId: string | null;
        createdAt: string;
        sentAt: string | null;
        lastError: string | null;
        attempts: number;
      }>;
    }>(res);
  },

  async retryMerchantPayout(payoutId: string) {
    const res = await fetch(`/api/admin/payouts/${payoutId}/retry`, {
      method: 'POST',
      credentials: 'include',
    });
    return handleResponse<{ success: boolean; message: string }>(res);
  },

  async retryAdminWithdrawal(withdrawalId: string) {
    const res = await fetch(`/api/admin/withdrawals/${withdrawalId}/retry`, {
      method: 'POST',
      credentials: 'include',
    });
    return handleResponse<{ id: string; status: string }>(res);
  },

  async completeAdminWithdrawal(withdrawalId: string) {
    const res = await fetch(`/api/admin/withdrawals/${withdrawalId}/complete`, {
      method: 'POST',
      credentials: 'include',
    });
    return handleResponse<{ id: string; status: string }>(res);
  },

  // Convenience alias used by dashboard bundle to fetch combined balance history
  async getTransactions(limit?: number, offset?: number) {
    return this.getBalanceHistory(limit, offset);
  },

  async getSavedWithdrawalAddresses() {
    const res = await fetch('/api/balance/withdrawals/saved', { credentials: 'include' });
    return handleResponse<{ addresses: string[] }>(res);
  },

  async createWithdrawal(amount: number, withdrawalAddress: string) {
    const res = await fetch('/api/balance/withdraw', {
      method: 'POST',
      headers: JSON_HEADERS,
      credentials: 'include',
      body: JSON.stringify({ amount, withdrawalAddress }),
    });
    return handleResponse<{
      id: string;
      amountUsdt: number;
      withdrawalAddress: string;
      status: string;
      txId: string | null;
      errorMessage: string | null;
      createdAt: string;
      completedAt: string | null;
    }>(res);
  },

  // Admin API
  async getAdminStats(): Promise<AdminStats> {
    const res = await fetch('/api/admin/stats', { credentials: 'include' });
    return handleResponse<AdminStats>(res);
  },

  async getAdminOrders(
    limit?: number,
    offset?: number,
    status?: string,
  ): Promise<PaginatedResponse<AdminOrderItem>> {
    const params = new URLSearchParams();
    if (typeof limit === 'number') params.append('limit', String(limit));
    if (typeof offset === 'number') params.append('offset', String(offset));
    if (status) params.append('status', status);
    const query = params.toString() ? `?${params.toString()}` : '';
    const res = await fetch(`/api/admin/orders${query}`, { credentials: 'include' });
    return handleResponse<PaginatedResponse<AdminOrderItem>>(res);
  },

  async getAdminUsers(
    limit?: number,
    offset?: number,
  ): Promise<PaginatedResponse<AdminUserItem>> {
    const params = new URLSearchParams();
    if (typeof limit === 'number') params.append('limit', String(limit));
    if (typeof offset === 'number') params.append('offset', String(offset));
    const query = params.toString() ? `?${params.toString()}` : '';
    const res = await fetch(`/api/admin/users${query}`, { credentials: 'include' });
    return handleResponse<PaginatedResponse<AdminUserItem>>(res);
  },

  async toggleUserAdmin(userId: string, isAdmin: boolean) {
    const res = await fetch(`/api/admin/users/${userId}/admin`, {
      method: 'PUT',
      headers: JSON_HEADERS,
      credentials: 'include',
      body: JSON.stringify({ isAdmin }),
    });
    return handleResponse<{ id: string; telegramId: string; isAdmin: boolean }>(res);
  },

  async toggleServiceFeeExempt(userId: string, exempt: boolean) {
    const res = await fetch('/api/admin/users/service-fee', {
      method: 'POST',
      headers: JSON_HEADERS,
      credentials: 'include',
      body: JSON.stringify({ userId, exempt }),
    });
    return handleResponse<{ id: string; telegramId: string; isServiceFeeExempt: boolean }>(res);
  },

  async correctUserBalance(
    userId: string,
    amount: number,
    reason: string,
  ): Promise<{ transactionId: string; amount: number; newBalance: number; reason: string }> {
    const res = await fetch(`/api/admin/users/${userId}/balance`, {
      method: 'POST',
      headers: JSON_HEADERS,
      credentials: 'include',
      body: JSON.stringify({ amount, reason }),
    });
    return handleResponse<{ transactionId: string; amount: number; newBalance: number; reason: string }>(res);
  },

  async getAdminIntegrations(): Promise<AdminIntegration[]> {
    const res = await fetch('/api/admin/integrations', { credentials: 'include' });
    return handleResponse<AdminIntegration[]>(res);
  },

  async updateAdminIntegration(
    code: string,
    data: Partial<{ flatFeeUsdt: number; feePercent: number; isActive: boolean }>,
  ): Promise<AdminIntegration> {
    const res = await fetch(`/api/admin/integrations/${code}`, {
      method: 'PUT',
      headers: JSON_HEADERS,
      credentials: 'include',
      body: JSON.stringify(data),
    });
    return handleResponse<AdminIntegration>(res);
  },

  async getAdminPayouts(
    limit?: number,
    offset?: number,
    status?: string,
  ): Promise<PaginatedResponse<AdminPayoutItem>> {
    const params = new URLSearchParams();
    if (typeof limit === 'number') params.append('limit', String(limit));
    if (typeof offset === 'number') params.append('offset', String(offset));
    if (status) params.append('status', status);
    const query = params.toString() ? `?${params.toString()}` : '';
    const res = await fetch(`/api/admin/payouts${query}`, { credentials: 'include' });
    return handleResponse<PaginatedResponse<AdminPayoutItem>>(res);
  },

  async getAdminWithdrawals(
    limit?: number,
    offset?: number,
    status?: string,
  ): Promise<PaginatedResponse<AdminWithdrawalItem>> {
    const params = new URLSearchParams();
    if (typeof limit === 'number') params.append('limit', String(limit));
    if (typeof offset === 'number') params.append('offset', String(offset));
    if (status) params.append('status', status);
    const query = params.toString() ? `?${params.toString()}` : '';
    const res = await fetch(`/api/admin/withdrawals${query}`, { credentials: 'include' });
    return handleResponse<PaginatedResponse<AdminWithdrawalItem>>(res);
  },

  async confirmOrderManually(
    orderId: string,
    reason: string,
  ): Promise<{
    orderId: string;
    status: string;
    confirmedAt: string;
    creditedAmount: number;
    userId: string | null;
  }> {
    const res = await fetch(`/api/admin/orders/${orderId}/confirm`, {
      method: 'POST',
      headers: JSON_HEADERS,
      credentials: 'include',
      body: JSON.stringify({ reason }),
    });
    return handleResponse<{
      orderId: string;
      status: string;
      confirmedAt: string;
      creditedAmount: number;
      userId: string | null;
    }>(res);
  },

  // Raffle Admin API
  async getAdminRaffles(
    limit?: number,
    offset?: number,
    status?: string,
  ): Promise<AdminRaffleItem[]> {
    const params = new URLSearchParams();
    if (typeof limit === 'number') params.append('limit', String(limit));
    if (typeof offset === 'number') params.append('offset', String(offset));
    if (status) params.append('status', status);
    const query = params.toString() ? `?${params.toString()}` : '';
    const res = await fetch(`/api/admin/raffles${query}`, { credentials: 'include' });
    return handleResponse<AdminRaffleItem[]>(res);
  },

  async getAdminRaffleStats(): Promise<RaffleStats> {
    const res = await fetch('/api/admin/raffles/stats/summary', { credentials: 'include' });
    return handleResponse<RaffleStats>(res);
  },

  async getRaffleConfig(): Promise<RaffleConfig> {
    const res = await fetch('/api/admin/raffles/config/current', { credentials: 'include' });
    return handleResponse<RaffleConfig>(res);
  },

  async updateRaffleConfig(data: Partial<Omit<RaffleConfig, 'id' | 'updatedAt'>>): Promise<RaffleConfig> {
    const res = await fetch('/api/admin/raffles/config/current', {
      method: 'PATCH',
      headers: JSON_HEADERS,
      credentials: 'include',
      body: JSON.stringify(data),
    });
    return handleResponse<RaffleConfig>(res);
  },

  async getRaffleWithTransactions(raffleId: string): Promise<RaffleWithTransactions> {
    const res = await fetch(`/api/admin/raffles/${raffleId}/transactions`, {
      credentials: 'include',
    });
    return handleResponse<RaffleWithTransactions>(res);
  },

  async resendWinnerNotification(raffleId: string): Promise<{ success: boolean; error?: string }> {
    const res = await fetch(`/api/admin/raffles/${raffleId}/resend-winner-notification`, {
      method: 'POST',
      credentials: 'include',
    });
    return handleResponse<{ success: boolean; error?: string }>(res);
  },

  async updateRaffleChatMessage(raffleId: string): Promise<{ success: boolean; error?: string }> {
    const res = await fetch(`/api/admin/raffles/${raffleId}/update-chat-message`, {
      method: 'POST',
      credentials: 'include',
    });
    return handleResponse<{ success: boolean; error?: string }>(res);
  },

  // Homepage Clients
  async getAdminClients(): Promise<AdminClientItem[]> {
    const res = await fetch('/api/admin/clients', { credentials: 'include' });
    return handleResponse<AdminClientItem[]>(res);
  },

  async updateAdminClient(
    userId: string,
    data: { showOnHomepage?: boolean; companyName?: string; companyUrl?: string; companyLogo?: string },
  ): Promise<AdminClientItem> {
    const res = await fetch(`/api/admin/clients/${userId}`, {
      method: 'PATCH',
      headers: JSON_HEADERS,
      credentials: 'include',
      body: JSON.stringify(data),
    });
    return handleResponse<AdminClientItem>(res);
  },

  // Admin Merchants API
  async getAdminMerchants(): Promise<Array<{
    id: string;
    username: string | null;
    telegramId: string;
    merchantFeePercent: number | null;
    createdAt: string;
    _count: { merchantApiTokens: number };
  }>> {
    const res = await fetch('/api/admin/merchants', { credentials: 'include' });
    return handleResponse(res);
  },

  async setAdminMerchantFee(
    userId: string,
    feePercent: number | null,
  ): Promise<{ id: string; telegramId: string; username: string | null; merchantFeePercent: number | null }> {
    const res = await fetch(`/api/admin/merchants/${userId}`, {
      method: 'PATCH',
      headers: JSON_HEADERS,
      credentials: 'include',
      body: JSON.stringify({ feePercent }),
    });
    return handleResponse(res);
  },

  // User Raffles API
  async getMyRaffles(): Promise<{
    raffles: Array<{
      id: string;
      raffleId: string;
      prizeAmountUsdt: number;
      status: string;
      scheduledAt: string;
      createdAt: string;
      completedAt: string | null;
      participantCount: number;
      winnerUsername: string | null;
      language: string;
    }>;
  }> {
    const res = await fetch('/api/raffles/my', { credentials: 'include' });
    return handleResponse(res);
  },

  async cancelMyRaffle(raffleId: string): Promise<{ success: boolean; error?: string }> {
    const res = await fetch(`/api/raffles/${raffleId}/cancel`, {
      method: 'POST',
      credentials: 'include',
    });
    return handleResponse(res);
  },
};
